import './InstanceAncestorTree.scss';
import { type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faSearch, faXmark } from '@fortawesome/free-solid-svg-icons';
import { TreeView } from 'components/TreeView/TreeView';
import StatusBadge from 'components/base/StatusBadge';
import { TruncatedText } from 'components/base/TruncatedText';
import { type NodeInstanceSummary } from 'api/node_instances/operations/getNodeInstanceTree';
import { InstanceTreeModel, type InstanceTreeNode } from './InstanceTreeModel';

/**
 * The status string the StatusBadge renders, mirroring the node-instances
 * table (nodeInstances.tsx): a sub-status is appended as a "(…)" suffix the
 * badge parses, and the "Console" sub-status surfaces as "Waiting" even though
 * the DB status stays 'started' for the flow engine.
 */
const displayStatus = (summary: NodeInstanceSummary): string => {
  const effective = summary.subStatus === 'Console' ? 'Waiting' : summary.status;
  return summary.subStatus ? `${effective} (${summary.subStatus})` : summary.status;
};

/** Wraps occurrences of `term` in the text with a highlight mark. */
const highlight = (text: string, term: string): ReactNode => {
  const needle = term.trim().toLowerCase();
  if (needle === '') return text;
  const lower = text.toLowerCase();
  const out: ReactNode[] = [];
  let from = 0;
  let key = 0;
  for (;;) {
    const at = lower.indexOf(needle, from);
    if (at === -1) {
      out.push(text.slice(from));
      break;
    }
    if (at > from) out.push(text.slice(from, at));
    out.push(
      <mark key={key++} className="instance-tree-match">
        {text.slice(at, at + needle.length)}
      </mark>
    );
    from = at + needle.length;
  }
  return out;
};

/**
 * Left-side tree navigator: the ancestor chain of the active instance and the
 * siblings at every level, with a live name/id filter. A pure observer of an
 * InstanceTreeModel.
 */
export const InstanceAncestorTree = observer(({ model }: { model: InstanceTreeModel }) => {
  const renderLabel = (node: InstanceTreeNode): ReactNode => {
    if (!node.summary) {
      return <span className="instance-tree-missing">…(missing)</span>;
    }
    return (
      <span className="instance-tree-label">
        <span className="instance-tree-name">
          <TruncatedText text={node.summary.name}>{highlight(node.summary.name, model.filter)}</TruncatedText>
        </span>
        <span className="instance-tree-id">({highlight(String(node.id), model.filter)})</span>
        <span className="instance-tree-badge">
          <StatusBadge status={displayStatus(node.summary)} />
        </span>
      </span>
    );
  };

  return (
    <div className="instance-ancestor-tree">
      <div className="instance-tree-filter">
        <div className="instance-tree-filter-field">
          <FontAwesomeIcon icon={faSearch} className="instance-tree-search-icon" />
          <input
            className="instance-tree-filter-input"
            type="text"
            placeholder="Filter"
            value={model.filter}
            onChange={(event) => model.setFilter(event.target.value)}
          />
          {model.filter !== '' && (
            <button type="button" className="instance-tree-clear" aria-label="Clear filter" onClick={() => model.setFilter('')}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
        <button
          type="button"
          className="instance-tree-refresh"
          aria-label="Refresh"
          title="Refresh the tree and the instance"
          disabled={model.loading}
          onClick={model.refresh}
        >
          <FontAwesomeIcon icon={faArrowsRotate} spin={model.loading} />
        </button>
      </div>
      <div className="instance-tree-matchcount">{model.filtering ? `${model.matchCount} matches (ancestors shown)` : ' '}</div>
      <div className="instance-tree-body">
        {!model.ready && model.loading && (
          <div className="instance-tree-status">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {model.error && (
          <Alert variant="danger" className="m-2">
            {model.error}
          </Alert>
        )}
        {model.ready && (
          <TreeView
            roots={model.roots}
            getId={(node) => node.id}
            getChildren={(node) => node.children}
            renderLabel={renderLabel}
            expandedIds={model.expandedIds}
            onToggle={model.toggleExpand}
            selectedId={model.activeId}
            onSelect={(node) => {
              if (node.summary) model.setActive(node.id);
            }}
            hasChildren={(node) => node.hasChildren}
          />
        )}
      </div>
    </div>
  );
});

export default InstanceAncestorTree;
